import React, { useState, useEffect, Fragment, useMemo, useCallback } from "react";
import { Link, withRouter } from "react-router-dom";
import TitleBar from "../../layout/TitleBar";
import Layout from "../../layout/Layout";
import Alert from "../../layout/Alert";
import { useDispatch, useSelector } from "react-redux";
import { getCancelRescheduleRequestById, addResponseRequest } from "../../../actions/admin/cancelreschedulerequest";
import LoadingOverlay from "react-loading-overlay";
import ScaleLoader from "react-spinners/ScaleLoader";
import Moment from "react-moment";
import { useLoading } from "../../../components/LoadingContext";

const CHANGES_MAPPING = {
  pickupLocation: ["pickupTime", "dropoffTime", "startFromGarage", "garageArrivalTime", "pickupLocation", "totalPrice"],
  destinations: ["dropoffTime", "garageArrivalTime", "destinations", "totalPrice"],
  pickupTime: ["pickupTime", "dropoffTime", "startFromGarage", "garageArrivalTime", "totalPrice"],
  travelDate: ["pickupTime", "dropoffTime", "startFromGarage", "garageArrivalTime", "totalPrice"],
  duration: ["dropoffTime", "garageArrivalTime", "totalPrice", "duration"],
};

const BOOKING_STATUS_MAP = {
  CONFIRMED: { text: "Confirmed", style: "badge bg-success py-2" },
  CANCELLED: { text: "Cancelled", style: "badge bg-danger py-2" },
};

const PAYMENT_STATUS_MAP = {
  CHARGED: { text: "Charged", style: "badge bg-success py-2" },
  ADDED_TO_MONTHLY_TOTAL: { text: "Charged", style: "badge bg-success py-2" },
  CHARGE_PENDING: { text: "Charge Pending", style: "badge bg-warning py-2" },
};

const REQUEST_STATUS_MAP = {
  PENDING_APPROVAL: { text: "Waiting for Approval", style: "badge bg-warning py-2 tw:!text-[#FFFFFF]" },
  APPROVED: { text: "Approved", style: "badge bg-success py-2" },
  REJECTED: { text: "Rejected", style: "badge bg-danger py-2" },
};

const ManageCancelRescheduleRequest = ({ match, history }) => {
  const dispatch = useDispatch();
  const [isloading, setIsloading] = useState(false);
  const { showLoading, closeLoading } = useLoading();
  const { loading, cancelreschedulerequest } = useSelector((state) => state.cancelreschedule);
  const [dataIndex, setDataIndex] = useState(0);
  const [dataShow, setDataShow] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    showLoading(true);
    dispatch(getCancelRescheduleRequestById(match.params.id));
  }, [dispatch, match.params.id]);

  useEffect(() => {
    if (!loading && cancelreschedulerequest !== null) {
      setDataShow(cancelreschedulerequest);
      closeLoading(true);
    }
  }, [cancelreschedulerequest])

  useEffect(() => {
    const data = getNestedByIndex(cancelreschedulerequest, dataIndex);
    setDataShow(data);

    setIsloading(false);
  }, [dataIndex])

  const countHistoryData = (obj) => {
    let count = 0;
    let current = obj;
    while (current?.previousData) {
      count++;
      current = current.previousData;
    }
    return count;
  };

  const hasHistory = (data) => {
    const count = countHistoryData(data);
    return { result: count > 0, count };
  };

  const handleChangeIndex = (ind) => {
    setIsloading(true);
    setDataIndex(ind);
  }

  const getNestedByIndex = (obj, index) => {
    let current = obj;
    for (let i = 0; i < index && current; i++) {
      current = current.previousData;
    }
    return current || null;
  };

  const changedFields = useMemo(() => {
    if (!cancelreschedulerequest || cancelreschedulerequest.requestType === "cancel") return new Set();
    const changes = cancelreschedulerequest.changes || {};
    return new Set(
      Object.entries(changes)
        .filter(([key, value]) => value && CHANGES_MAPPING[key])
        .flatMap(([key]) => CHANGES_MAPPING[key])
    );
  }, [cancelreschedulerequest]);

  const getChangedValue = (type) => changedFields.has(type);

  const getCurrentBookingStatus = (status) => BOOKING_STATUS_MAP[status] || { text: status, style: "badge bg-secondary py-2" };

  const getCurrentBookingPaymentStatus = (status) => PAYMENT_STATUS_MAP[status] || { text: status, style: "badge bg-secondary py-2" };

  const getRequestStatus = (status, prevBookingStatus) => {
    if (status === "APPROVED" && prevBookingStatus === "CHARGE_PENDING") {
      return { text: "Auto Approved", style: "badge bg-success py-2" };
    }
    return REQUEST_STATUS_MAP[status] || { text: status, style: "badge bg-secondary py-2" };
  };

  const getRequestPaymentStatus = (paymentStatus, refundStatus) => {
    if (paymentStatus === "CHARGE_PENDING") return { text: "Charge Pending", style: "bg-warning" };
    if (paymentStatus === "CHARGED" || paymentStatus === "ADDED_TO_MONTHLY_TOTAL") return { text: "Charged", style: "bg-success" };
    if (refundStatus === "REFUND_PENDING") return { text: "Refund Pending", style: "bg-warning" };
    if (refundStatus === "REFUNDED") return { text: "Refunded", style: "bg-success" };
    return { text: "No need to charge or refund", style: "bg-success" };
  };

  const getApprovedButtonText = useCallback((status, paymentStatus, refundStatus) => {
    const isDeferredEligible = cancelreschedulerequest?.userId?.deferredPaymentEligible;
    const noCharge = paymentStatus === "NO_NEED_CHARGE";
    const noRefund = refundStatus === "NO_NEED_REFUND";

    if (status === "PENDING_APPROVAL") {
      if (isDeferredEligible || (noCharge && noRefund)) return { text: "Accept!", disabled: false };
      if (paymentStatus === "CHARGE_PENDING" && noRefund) return { text: "Charge! - JPY " + cancelreschedulerequest?.chargeAmount?.toLocaleString(), disabled: false };
      if (noCharge && refundStatus === "REFUND_PENDING") return { text: "Accept!", disabled: false };
    }

    if (status === "APPROVED") {
      if ((paymentStatus === "CHARGED" || paymentStatus === "ADDED_TO_MONTHLY_TOTAL") && noRefund) {
        return { text: "Charged - JPY " + cancelreschedulerequest?.flywireChargeResponse?.amount?.toLocaleString(), disabled: true };
      }
      return { text: "Accepted", disabled: true };
    }

    return { text: "Rejected", disabled: true };
  }, [cancelreschedulerequest]);

  const adminResponseHandler = async (id, response) => {
    showLoading(true);
    const chargeTokenRes = await addResponseRequest({ requestId: id, status: response, notes: note });
    closeLoading(true);

    if (chargeTokenRes.data.status === "success") {
      window.location.reload();
    } else {
      console.error("Response failed:", chargeTokenRes.data);
    }
  }

  return (
    <LoadingOverlay
      active={isloading}
      spinner={<ScaleLoader color="#36D7B7" />}
    >
      <Layout>
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 w-100">
          <TitleBar title="Manage Request Cancellation or Reschedule" />
          {!loading && dataShow !== null && (
            <Fragment>
              <div className="container-fluid">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <div className="col-sm-offset-2 col-sm-10">
                        <Link
                          className="btn btn-secondary"
                          to="/admin/cancel-reschedule-request"
                        >
                          <i
                            className="fa fa-arrow-left fa-fw"
                            aria-hidden="true"
                          ></i>{" "}
                          Back To List
                        </Link>
                      </div>
                    </div>
                    {/* Customer Information */}
                    <div className="card text-dark mb-3">
                      <div className="card-header">Customer's Billing Information</div>
                      <div className="card-body">
                        <div className="row mb-3">
                          <div className="col">
                            <h6 className="card-title">Name</h6>
                            <p className="card-text py-1">{cancelreschedulerequest.bookingId?.customer?.name}</p>
                          </div>
                          <div className="col">
                            <h6 className="card-title">Phone</h6>
                            <p className="card-text py-1">{`(${cancelreschedulerequest.bookingId?.customer?.prefix_number}) ${cancelreschedulerequest.bookingId?.customer?.phone}`}</p>
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col">
                            <h6 className="card-title">Email</h6>
                            <p className="card-text py-1">{cancelreschedulerequest.bookingId?.customer?.email}</p>
                          </div>
                          <div className="col">
                            <h6 className="card-title">Type</h6>
                            <p className="card-text py-1">{cancelreschedulerequest?.userId?.role}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {
                    hasHistory(cancelreschedulerequest).result && (
                      <div className="col-md-12">
                        <div className="mb-3">
                          {Array.from({ length: (hasHistory(cancelreschedulerequest).count + 1) }).map((_, index) => (
                            <button
                              key={index}
                              className={`btn btn-secondary ms-1 ${index === dataIndex ? 'active' : ''}`}
                              onClick={() => handleChangeIndex(index)}
                            >
                              {
                                index === 0 ? "Latest Request" : (
                                  <>
                                    Edit Request on <Moment format="YYYY/MM/DD" tz="Asia/Tokyo">{dataShow.date}</Moment>
                                  </>
                                )
                              }
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  }

                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header">
                        Booking Information{" "}
                        <span
                          style={{
                            border:
                              "1px solid #AAAAA",
                            borderRadius: "5px",
                            paddingLeft: "10px",
                            paddingRight: "10px",
                            paddingTop: "5px",
                            paddingBottom: "5px",
                          }}
                        >
                          Cart ID : {cancelreschedulerequest.bookingId?.cartID}
                        </span>
                      </div>
                      <div className="card-body text-dark">
                        <div className="row">
                          <div className="col mb-2">
                            <div className="row mb-2 d-flex justify-content-center gap-3">
                              <div className="col-auto">
                                <h6 className="card-title text-center">Booking Status</h6>
                                <p className={`card-text py-1 d-flex justify-content-center ${getCurrentBookingStatus(cancelreschedulerequest.bookingId.bookingStatus).style}`}>
                                  {getCurrentBookingStatus(cancelreschedulerequest.bookingId.bookingStatus).text}
                                </p>
                              </div>
                              <div className="col-auto">
                                <h6 className="card-title">Payment Status</h6>
                                <p className={`card-text py-1 ${getCurrentBookingPaymentStatus(cancelreschedulerequest.bookingId.paymentStatus).style}`}>
                                  {getCurrentBookingPaymentStatus(cancelreschedulerequest.bookingId.paymentStatus).text}
                                </p>
                              </div>
                              <div className="col-auto">
                                <h6 className="card-title">Booking Type</h6>
                                <p className={`card-text py-1`}>
                                  {cancelreschedulerequest.bookingId.bookingType === "byTheHour" ? "By The Hour" : cancelreschedulerequest.bookingId.isAirportTransfer ? "Airport Transfer" : "Specified Destination"}
                                </p>
                              </div>
                            </div>
                            <hr />
                            <h6 className={`card-title text-center ${getChangedValue("pickupTime") && 'text-gold'}`}>Pickup Time</h6>
                            <div className="row mb-3">
                              <div className="col d-flex align-items-center justify-content-center">
                                <p className="card-text py-1 mb-2">
                                  <Moment format="YYYY/MM/DD HH:mm">{dataShow.previousBooking ? dataShow.previousBooking.pickupDateTime : cancelreschedulerequest.bookingId.pickupDateTime}</Moment>
                                </p>
                              </div>
                              {dataShow.requestType == "reschedule" && (
                                <>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <h6 className="card-title">
                                      <i
                                        className="fa fa-arrow-right fa-fw"
                                        aria-hidden="true"
                                      ></i>{" "}
                                    </h6>
                                  </div>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <p className={`card-text py-1 mb-2 ${getChangedValue("pickupTime") && 'fw-700'}`}>
                                      <Moment format="YYYY/MM/DD HH:mm">{dataShow.editBooking.pickupDateTime}</Moment>
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                            {dataShow.editBooking.bookingType === "byTheHour" && (
                              <>
                                <h6 className={`card-title text-center ${getChangedValue("duration") && 'text-gold'}`}>Duration </h6>
                                <div className="row mb-3">
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <p className="card-text py-1 mb-2">
                                      {dataShow.previousBooking ? dataShow.previousBooking.duration : cancelreschedulerequest.bookingId.duration}
                                    </p>
                                  </div>
                                  {dataShow.requestType == "reschedule" && (
                                    <>
                                      <div className="col d-flex align-items-center justify-content-center">
                                        <h6 className="card-title">
                                          <i
                                            className="fa fa-arrow-right fa-fw"
                                            aria-hidden="true"
                                          ></i>{" "}
                                        </h6>
                                      </div>
                                      <div className="col d-flex align-items-center justify-content-center">
                                        <p className={`card-text py-1 mb-2 ${getChangedValue("duration") && 'fw-700'}`}>
                                          {dataShow.editBooking.duration}
                                        </p>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                            <h6 className={`card-title text-center ${getChangedValue("dropoffTime") && 'text-gold'}`}>Dropoff Time</h6>
                            <div className="row mb-3">
                              <div className="col d-flex align-items-center justify-content-center">
                                <p className="card-text py-1 mb-2">
                                  <Moment format="YYYY/MM/DD HH:mm">{dataShow.previousBooking ? dataShow.previousBooking.customerDropoffDateTime : cancelreschedulerequest.bookingId?.customerDropoffDateTime}</Moment>
                                </p>
                              </div>
                              {dataShow.requestType == "reschedule" && (
                                <>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <h6 className="card-title text-center">
                                      <i
                                        className="fa fa-arrow-right fa-fw"
                                        aria-hidden="true"
                                      ></i>{" "}
                                    </h6>
                                  </div>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <p className={`card-text py-1 mb-2 ${getChangedValue("dropoffTime") && 'fw-700'}`}>
                                      <Moment format="YYYY/MM/DD HH:mm">{dataShow.editBooking.customerDropoffDateTime}</Moment>
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                            <h6 className={`card-title text-center ${getChangedValue("startFromGarage") && 'text-gold'}`}>Calculated dispatch from Garage</h6>
                            <div className="row mb-3">
                              <div className="col d-flex align-items-center justify-content-center">
                                <p className="card-text py-1 mb-2">
                                  <Moment format="YYYY/MM/DD HH:mm">{dataShow.previousBooking ? dataShow.previousBooking.startFromGarage : cancelreschedulerequest.bookingId.startFromGarage}</Moment>
                                </p>
                              </div>
                              {dataShow.requestType == "reschedule" && (
                                <>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <h6 className="card-title text-center">
                                      <i
                                        className="fa fa-arrow-right fa-fw"
                                        aria-hidden="true"
                                      ></i>{" "}
                                    </h6>
                                  </div>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <p className={`card-text py-1 mb-2 ${getChangedValue("startFromGarage") && 'fw-700'}`}>
                                      <Moment format="YYYY/MM/DD HH:mm">{dataShow.editBooking.startFromGarage}</Moment>
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                            <h6 className={`card-title text-center ${getChangedValue("garageArrivalTime") && 'text-gold'}`}>Calculated arrival at Garage</h6>
                            <div className="row mb-3">
                              <div className="col d-flex align-items-center justify-content-center">
                                <p className="card-text py-1 mb-2">
                                  <Moment format="YYYY/MM/DD HH:mm">{dataShow.previousBooking ? dataShow.previousBooking.garageArivalTime : cancelreschedulerequest.bookingId.garageArivalTime}</Moment>
                                </p>
                              </div>
                              {dataShow.requestType == "reschedule" && (
                                <>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <h6 className="card-title text-center">
                                      <i
                                        className="fa fa-arrow-right fa-fw"
                                        aria-hidden="true"
                                      ></i>{" "}
                                    </h6>
                                  </div>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <p className={`card-text py-1 mb-2 ${getChangedValue("garageArrivalTime") && 'fw-700'}`}>
                                      <Moment format="YYYY/MM/DD HH:mm">{dataShow.editBooking.garageArivalTime}</Moment>
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                            <h6 className={`card-title text-center ${getChangedValue("pickupLocation") && 'text-gold'}`}>Pickup Location</h6>
                            <div className="row mb-3">
                              <div className="col d-flex align-items-center justify-content-center">
                                <p className="card-text py-1" style={{
                                  textAlign: 'center'
                                }}>
                                  {dataShow.previousBooking ? dataShow.previousBooking.pickupLocation.label : cancelreschedulerequest.bookingId.pickupLocation.label}
                                </p>
                              </div>
                              {dataShow.requestType == "reschedule" && (
                                <>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <h6 className="card-title text-center">
                                      <i
                                        className="fa fa-arrow-right fa-fw"
                                        aria-hidden="true"
                                      ></i>{" "}
                                    </h6>
                                  </div>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <p className={`card-text py-1 mb-2 ${getChangedValue("pickupLocation") && 'fw-700'}`} style={{
                                      textAlign: 'center'
                                    }}>
                                      {dataShow.editBooking.pickupLocation.label}
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>

                            <h6 className={`card-title text-center ${getChangedValue("destinations") && 'text-gold'}`}>Destinations</h6>
                            <div className="row mb-3">
                              <div className="col d-flex align-items-center justify-content-center">
                                <p className="card-text py-1" style={{
                                  textAlign: 'center'
                                }}>
                                  {dataShow?.historyId ?
                                    dataShow.historyId.previousBooking.destinations.map((d) => (
                                      <p key={d._id} className="card-text py-1">
                                        {d.label}
                                      </p>
                                    )) :
                                    cancelreschedulerequest.bookingId.destinations.map((d) => (
                                      <p key={d._id} className="card-text py-1">
                                        {d.label}
                                      </p>
                                    ))}
                                </p>
                              </div>
                              {dataShow.requestType == "reschedule" && (
                                <>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <h6 className="card-title text-center">
                                      <i
                                        className="fa fa-arrow-right fa-fw"
                                        aria-hidden="true"
                                      ></i>{" "}
                                    </h6>
                                  </div>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    {dataShow.editBooking.destinations ? (
                                      dataShow.editBooking.destinations.map((d, i) => (
                                        <p key={i} className={`card-text py-1 ${getChangedValue("destinations") && 'fw-700'}`} style={{
                                          textAlign: 'center'
                                        }}>
                                          {d.label}
                                        </p>
                                      ))
                                    ) : (
                                      <h4>No destinations found...</h4>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            <h6 className={`card-title text-center ${getChangedValue("totalPrice") && 'text-gold'}`}>Total Price</h6>
                            <div className="row mb-3">
                              <div className="col d-flex align-items-center justify-content-center">
                                <p className="card-text py-1">
                                  {"JPY " + (dataShow?.previousBooking ? dataShow?.previousBooking?.totalPrice?.toLocaleString() : cancelreschedulerequest?.bookingId?.totalPrice?.toLocaleString())}
                                </p>
                              </div>
                              {dataShow.requestType == "reschedule" && (
                                <>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <h6 className="card-title text-center">
                                      <i
                                        className="fa fa-arrow-right fa-fw"
                                        aria-hidden="true"
                                      ></i>{" "}
                                    </h6>
                                  </div>
                                  <div className="col d-flex align-items-center justify-content-center">
                                    <p className={`card-text py-1 ${getChangedValue("totalPrice") && 'fw-700'}`}>
                                      {"JPY " + dataShow?.editBooking?.totalPrice?.toLocaleString()}
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <hr />
                        <Link
                          className="btn btn-secondary d-flex justify-content-center"
                          to={"/admin/bookinglist/edit/v2/" + cancelreschedulerequest.bookingId._id}
                          target="_blank"
                        >
                          Booking Detail Page
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header">Request details | created at : <Moment format="YYYY/MM/DD HH:mm" tz="Asia/Tokyo">{dataShow.date}</Moment></div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col mb-2">
                            <div className="row mb-2">
                              <div className="col">
                                <h6 className="card-title">Request Status</h6>
                                <p className={`card-text py-1 ${getRequestStatus(dataShow.status, dataShow.previousBooking?.paymentStatus).style}`}>
                                  {getRequestStatus(dataShow.status, dataShow.previousBooking?.paymentStatus).text}
                                </p>
                              </div>
                              <div className="col">
                                <h6 className="card-title">Request Type</h6>
                                {dataShow.requestType == "reschedule" ? (
                                  <span className="badge bg-info py-2 mb-2">Reschedule</span>
                                ) : (
                                  <span className="badge bg-primary py-2 mb-2">Cancellation</span>
                                )}
                              </div>
                            </div>
                            <div className="row mb-2">
                              {dataShow?.cancellationPercent > 0 && (
                                <div className="col">
                                  <h6 className="card-title">Cancellation Fee(%)</h6>
                                  <p className="card-text py-1">
                                    {`${dataShow?.cancellationPercent || 0}%`}
                                  </p>
                                </div>
                              )}
                              {dataShow?.cancellationFee > 0 && (
                                <div className="col">
                                  <h6 className="card-title">Cancellation Fee (JPY)</h6>
                                  <p className="card-text py-1">
                                    {`JPY ${dataShow?.cancellationFee?.toLocaleString() || 0}`}
                                  </p>
                                </div>
                              )}
                              {dataShow?.meetAssistCancellationPercent > 0 && (
                                <div className="col">
                                  <h6 className="card-title">Meet & Assist Cancellation Fee(%)</h6>
                                  <p className="card-text py-1">
                                    {`${dataShow?.meetAssistCancellationPercent || 0}%`}
                                  </p>
                                </div>
                              )}
                              {dataShow?.meetAssistCancellationFee > 0 && (
                                <div className="col">
                                  <h6 className="card-title">Meet & Assist Cancellation Fee (JPY)</h6>
                                  <p className="card-text py-1">
                                    {`JPY ${dataShow?.meetAssistCancellationFee?.toLocaleString() || 0}`}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="row mb-4">
                              <div className="col">
                                <h6 className="card-title">Payment Status</h6>
                                <span className={`badge ${getRequestPaymentStatus(dataShow.paymentStatus, dataShow.refundStatus).style} py-2`}>{getRequestPaymentStatus(dataShow.paymentStatus, dataShow.refundStatus).text}</span>
                              </div>
                            </div>
                            {
                              dataShow.notes && (
                                <div className="row mb-4">
                                  <div className="col">
                                    <label className="label mb-2">
                                      Notes:
                                    </label>
                                    {dataShow.notes}
                                  </div>
                                </div>
                              )
                            }
                            {
                              dataIndex === 0 && (
                                <>
                                  <div className="row mb-2">
                                    <div className="col">
                                      <div className="mb-3">
                                        <label className="label mb-2">
                                          Notes:
                                        </label>
                                        <div className="col-sm-12">
                                          <textarea
                                            className="form-control form-control-admin"
                                            id="reg"
                                            placeholder="Enter notes for reject message"
                                            name="note"
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row mb-2">
                                    <div className="col">
                                      <button
                                        disabled={getApprovedButtonText(dataShow.status, dataShow.paymentStatus, dataShow.refundStatus).disabled}
                                        className="btn btn-secondary ms-1"
                                        onClick={() => { adminResponseHandler(dataShow._id, "approved") }}
                                      >
                                        {
                                          getApprovedButtonText(dataShow.status, dataShow.paymentStatus, dataShow.refundStatus).text !== "Approve!" ?
                                            (
                                              <>
                                                <i
                                                  className="fa fa-money fa-fw"
                                                  aria-hidden="true"
                                                ></i>
                                                &nbsp;
                                                {getApprovedButtonText(dataShow.status, dataShow.paymentStatus, dataShow.refundStatus).text}
                                              </>
                                            ) : (
                                              getApprovedButtonText(dataShow.status, dataShow.paymentStatus, dataShow.refundStatus).text
                                            )
                                        }
                                      </button>
                                      {
                                        dataShow.requestType === "reschedule" && (
                                          <button
                                            disabled={dataShow.status !== "PENDING_APPROVAL"}
                                            className="btn btn-danger ms-1"
                                            onClick={() => { adminResponseHandler(dataShow._id, "rejected") }}
                                          >
                                            <p className="noto m-0 text-white">Reject</p>
                                          </button>
                                        )
                                      }
                                    </div>
                                  </div>
                                </>
                              )
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>
          )}
          <Alert />
        </main>
      </Layout>
    </LoadingOverlay>
  );
};

export default withRouter(ManageCancelRescheduleRequest);
